import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  useTheme,
} from '@mui/material';
import { HeadsetMic as HeadsetMicIcon, WhatsApp as WhatsAppIcon } from '@mui/icons-material';
import { atendimentoService, AtendimentoTicket } from '../../services/atendimentoService';
import VisualizarTicketDialog from '../../pages/atendimento/componentes/VisualizarTicketDialog';

export interface AtendimentosRankingListaFilters {
  contato_id: number;
  data_criacao_inicio?: string;
  data_criacao_fim?: string;
  setor_id?: number;
}

interface AtendimentosRankingListaDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  filters: AtendimentosRankingListaFilters;
}

const CORES = ['#5c6bc0', '#26a69a', '#ef5350', '#ff7043', '#ab47bc', '#29b6f6', '#66bb6a', '#ffa726'];

const getInitials = (name: string) =>
  name ? name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase() : '?';

const getStatusLabel = (status: AtendimentoTicket['status']) => {
  const labels: Record<AtendimentoTicket['status'], string> = {
    pendente: 'Aguardando',
    em_atendimento: 'Em atendimento',
    fechado: 'Fechado',
    fechado_bot: 'Fechado (Bot)',
  };
  return labels[status] || status;
};

const getStatusColor = (status: AtendimentoTicket['status']): 'default' | 'success' | 'warning' | 'info' => {
  switch (status) {
    case 'em_atendimento':
      return 'success';
    case 'pendente':
      return 'warning';
    case 'fechado_bot':
      return 'info';
    default:
      return 'default';
  }
};

const formatDate = (dateString?: string | null) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
};

const normalizeHexColor = (color?: string | null) => {
  if (!color) return '#1565c0';
  return color.startsWith('#') ? color : `#${color}`;
};

const AtendimentosRankingListaDialog: React.FC<AtendimentosRankingListaDialogProps> = ({
  open,
  onClose,
  title,
  subtitle,
  filters,
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState<AtendimentoTicket[]>([]);
  const [ticketVisualizacao, setTicketVisualizacao] = useState<AtendimentoTicket | null>(null);

  useEffect(() => {
    if (!open) return;

    let ativo = true;
    const carregar = async () => {
      setLoading(true);
      try {
        const data = await atendimentoService.getTickets({
          contato_id: filters.contato_id,
          data_criacao_inicio: filters.data_criacao_inicio,
          data_criacao_fim: filters.data_criacao_fim,
        });
        if (!ativo) return;
        let lista = Array.isArray(data) ? data : [];
        if (filters.setor_id) {
          lista = lista.filter((ticket) => ticket.setor_id === filters.setor_id);
        }
        setTickets(lista);
      } catch {
        if (ativo) setTickets([]);
      } finally {
        if (ativo) setLoading(false);
      }
    };

    carregar();
    return () => {
      ativo = false;
    };
  }, [open, filters]);

  useEffect(() => {
    if (!open) {
      setTickets([]);
      setTicketVisualizacao(null);
    }
  }, [open]);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ py: 1.25 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <HeadsetMicIcon color="primary" sx={{ fontSize: 20 }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={800} lineHeight={1.2}>
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="caption" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4} gap={1}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                Carregando atendimentos...
              </Typography>
            </Box>
          ) : tickets.length === 0 ? (
            <Box py={4} textAlign="center">
              <Typography variant="body2" color="text.secondary">
                Nenhum atendimento encontrado.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
              {tickets.map((ticket) => {
                const corAvatar = CORES[ticket.contato_id % CORES.length];
                const corSetor = normalizeHexColor(ticket.setor?.cor);
                return (
                  <Box
                    key={ticket.id}
                    onClick={() => setTicketVisualizacao(ticket)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.2,
                      px: 1.5,
                      py: 1,
                      cursor: 'pointer',
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Avatar
                      src={atendimentoService.getAvatarUrl(ticket.contato?.foto_perfil)}
                      sx={{ bgcolor: corAvatar, width: 36, height: 36, fontSize: '0.75rem', flexShrink: 0 }}
                    >
                      {!ticket.contato?.foto_perfil && getInitials(ticket.contato?.nome || '?')}
                    </Avatar>
                    <Box flex={1} minWidth={0}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                        <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem' }}>
                          #{ticket.id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                          {formatDate(ticket.createdAt)}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ fontSize: '0.7rem', mt: 0.2 }}>
                        {ticket.ultima_mensagem || 'Sem mensagem'}
                      </Typography>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={1}
                        mt={0.45}
                      >
                        <Box display="flex" alignItems="center" gap={0.5} minWidth={0} flexWrap="wrap">
                          {ticket.setor && (
                            <Chip
                              label={ticket.setor.nome}
                              size="small"
                              sx={{
                                height: 18,
                                maxWidth: 120,
                                bgcolor: corSetor,
                                color: '#fff',
                                fontSize: '0.65rem',
                                fontWeight: 600,
                              }}
                            />
                          )}
                          {ticket.conexao && (
                            <Chip
                              icon={<WhatsAppIcon sx={{ fontSize: '0.78rem !important' }} />}
                              label={ticket.conexao.nome}
                              size="small"
                              sx={{
                                height: 18,
                                maxWidth: 130,
                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(37,211,102,0.16)' : 'rgba(37,211,102,0.12)',
                                color: theme.palette.mode === 'dark' ? '#8ee6ab' : '#128c4a',
                                fontSize: '0.65rem',
                                fontWeight: 600,
                              }}
                            />
                          )}
                          {ticket.usuario ? (
                            <Typography variant="caption" color="primary.main" fontWeight={700} noWrap sx={{ fontSize: '0.65rem' }}>
                              <HeadsetMicIcon sx={{ fontSize: 13, verticalAlign: 'text-bottom', mr: 0.25 }} />
                              {ticket.usuario.nome}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.disabled" noWrap sx={{ fontSize: '0.65rem', fontStyle: 'italic' }}>
                              Sem atendente
                            </Typography>
                          )}
                        </Box>
                        <Chip
                          label={getStatusLabel(ticket.status)}
                          color={getStatusColor(ticket.status)}
                          size="small"
                          sx={{ height: 18, fontSize: '0.65rem', flexShrink: 0 }}
                        />
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
            {tickets.length} atendimento{tickets.length !== 1 ? 's' : ''}
          </Typography>
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <VisualizarTicketDialog
        open={Boolean(ticketVisualizacao)}
        ticket={ticketVisualizacao}
        onClose={() => setTicketVisualizacao(null)}
      />
    </>
  );
};

export default AtendimentosRankingListaDialog;
